import { useDispatch, useMachineState } from '../app/context';
import { Button } from '../components/Button';
import { cx } from '../components/cx';
import { CheckIcon } from '../components/icons';
import { bulbSummary } from '../domain/catalog';
import { formatClockMinutes, formatDuration, targetLabel } from '../domain/format';
import type { RecordProblem } from '../domain/types';
import common from './common.module.css';
import styles from './ReportScreen.module.css';

const NOT_CONFIRMED: Readonly<Record<RecordProblem, string>> = {
  unreachable:
    'The result could not be confirmed with the robot, so this room is not on record as sanitised. Check the connection and run the cycle again.',
  rejected:
    'The robot did not confirm a full exposure — the lamp may have been switched off during the cycle. Treat the room as NOT sanitised and run the cycle again.',
};

export function ReportScreen() {
  const { lastRecord, recordPending, recordProblem, lampChannels, operator } = useMachineState();
  const dispatch = useDispatch();
  if (!lastRecord) return null;
  // A service account's test run: the lamp ran its full time, but no room is on record as clean.
  const testRun = operator !== null && !operator.realCycles;

  const rows: ReadonlyArray<readonly [string, string]> = [
    ['ZONE / BED', targetLabel(lastRecord.wardCode, lastRecord.bed)],
    ['ROBOT POSITION', lastRecord.positionLabel],
    ['BULBS', bulbSummary(lastRecord.channels, lampChannels)],
    ['OPERATOR', lastRecord.operatorName.toUpperCase()],
    ['CYCLE START', formatClockMinutes(Date.parse(lastRecord.startedAt))],
    ['DURATION', formatDuration(lastRecord.durationSeconds)],
  ];

  return (
    <section className={cx(common.screen, common.fadeUp, styles.screen)} aria-label="Cycle report">
      {recordPending ? (
        <div className={cx(common.card, styles.summary)} role="status">
          <div className={cx(common.kicker, styles.warningKicker)}>CONFIRMING</div>
          <h1 className={styles.title}>Checking the Result</h1>
          <div className={styles.message}>
            The lamp is switching itself off. Keep the door closed until this screen confirms the
            full exposure.
          </div>
        </div>
      ) : recordProblem ? (
        <div className={cx(common.card, styles.summary)} role="alert">
          <div className={cx(common.kicker, styles.warningKicker)}>NOT RECORDED</div>
          <div className={cx(styles.badge, styles.badgeWarning)} aria-hidden>
            !
          </div>
          <h1 className={styles.title}>Cycle Not Confirmed</h1>
          <div className={styles.message}>{NOT_CONFIRMED[recordProblem]}</div>
        </div>
      ) : (
        <div className={cx(common.card, styles.summary)}>
          <div className={cx(common.kicker, common.reveal)}>
            {testRun ? 'TEST COMPLETE' : 'JOB COMPLETE'}
          </div>
          <div className={styles.badge} aria-hidden>
            <CheckIcon size={46} />
          </div>
          <h1 className={styles.title}>{testRun ? 'Test Cycle Done' : 'Cleaning Done!'}</h1>
          <div className={styles.message}>
            {testRun
              ? 'The lamp ran the full cycle. Service runs are tests: this room is not recorded as sanitised.'
              : 'The room is perfectly clean and ready for the next patient.'}
          </div>
        </div>
      )}

      <div className={styles.side}>
        <div className={cx(common.card, styles.report)}>
          <div className={cx(common.label, styles.reportHeading)}>
            CYCLE REPORT · LOG {lastRecord.logId}
          </div>
          <dl className={styles.rows}>
            {rows.map(([label, value]) => (
              <div key={label} className={styles.row}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className={styles.actions}>
          <Button
            variant="primary"
            size="cta"
            className={styles.next}
            onClick={() => dispatch({ type: 'LOG_AND_NEXT' })}
          >
            LOG &amp; START NEXT ROOM
          </Button>
          <Button variant="secondary" size="cta" onClick={() => dispatch({ type: 'STANDBY' })}>
            STANDBY
          </Button>
        </div>
      </div>
    </section>
  );
}
